import { Check, Copy, Shield } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { type FormEvent, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authClient } from '@/lib/auth'

export function TwoFactorSetup() {
  const [step, setStep] = useState<'password' | 'qr' | 'verify'>('password')
  const [password, setPassword] = useState('')
  const [totpUri, setTotpUri] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()

  const handleEnableTwoFactor = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await authClient.twoFactor.enable({ password })
      if (result.error) {
        setError(result.error.message ?? 'Failed to enable 2FA')
        setLoading(false)
        return
      }
      const data = result.data as { totpURI?: string; backupCodes?: string[] } | undefined
      setTotpUri(data?.totpURI ?? '')
      setBackupCodes(data?.backupCodes ?? [])
      setStep('qr')
      setLoading(false)
    } catch {
      setError('An unexpected error occurred')
      setLoading(false)
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const next = [...code]
    next[index] = value.slice(-1)
    setCode(next)
    if (value && index < 5) inputRefs.current[index + 1]?.focus()
  }

  const handleVerifyTotp = async (e: FormEvent) => {
    e.preventDefault()
    const totp = code.join('')
    if (totp.length !== 6) return
    setError('')
    setLoading(true)
    try {
      const result = await authClient.twoFactor.verifyTotp({ code: totp })
      if (result.error) {
        setError(result.error.message ?? 'Invalid code')
        setCode(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
        setLoading(false)
        return
      }
      navigate('/', { replace: true })
    } catch {
      setError('Verification failed')
      setLoading(false)
    }
  }

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center justify-center rounded-full border border-dashed border-border bg-surface p-3">
            <Shield className="h-6 w-6 text-accent" strokeWidth={1.5} />
          </div>
          <h1 className="font-mono text-xs font-medium tracking-widest text-ink uppercase">
            Set Up Two-Factor Authentication
          </h1>
          <p className="mt-2 font-mono text-[11px] text-ink-faint">
            Secure your admin account with a TOTP authenticator app
          </p>
        </div>

        <div className="rounded-lg border border-dashed border-border bg-surface p-6">
          {error && (
            <div className="mb-4 rounded-md border border-dashed border-negative/30 bg-negative/5 px-3 py-2 font-mono text-xs text-negative">
              {error}
            </div>
          )}

          {step === 'password' && (
            <form onSubmit={handleEnableTwoFactor} className="space-y-4">
              <div>
                <label
                  htmlFor="2fa-password"
                  className="block font-mono text-[11px] tracking-wider text-ink-faint uppercase"
                >
                  Confirm your password
                </label>
                <input
                  id="2fa-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2.5 font-mono text-xs text-ink"
                  placeholder="Enter your password"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-accent py-2.5 font-mono text-xs font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
              >
                {loading ? 'Setting up...' : 'Continue'}
              </button>
            </form>
          )}

          {step === 'qr' && (
            <div className="space-y-5">
              <div className="flex justify-center rounded-lg bg-white p-4">
                {totpUri && <QRCodeSVG value={totpUri} size={180} />}
              </div>

              <p className="text-center font-mono text-[11px] text-ink-muted">
                Scan this QR code with Google Authenticator, 1Password, or Authy
              </p>

              {backupCodes.length > 0 && (
                <div>
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                      Backup Codes
                    </p>
                    <button
                      type="button"
                      onClick={copyBackupCodes}
                      className="flex items-center gap-1 font-mono text-[11px] text-accent hover:text-accent-hover"
                    >
                      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5 rounded-md border border-dashed border-border bg-canvas p-3">
                    {backupCodes.map((c) => (
                      <span key={c} className="font-mono text-[11px] text-ink-muted">
                        {c}
                      </span>
                    ))}
                  </div>
                  <p className="mt-2 font-mono text-[10px] text-ink-faint">
                    Store these codes safely. Each can be used once if you lose your authenticator.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => setStep('verify')}
                className="w-full rounded-md bg-accent py-2.5 font-mono text-xs font-medium text-white transition-colors hover:bg-accent-hover"
              >
                I've scanned the QR code
              </button>
            </div>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerifyTotp} className="space-y-5">
              <p className="text-center font-mono text-[11px] text-ink-muted">
                Enter the 6-digit code from your authenticator to confirm setup
              </p>
              <div className="flex justify-center gap-2">
                {['d0', 'd1', 'd2', 'd3', 'd4', 'd5'].map((key, i) => (
                  <input
                    key={key}
                    ref={(el) => {
                      inputRefs.current[i] = el
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={code[i]}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !code[i] && i > 0)
                        inputRefs.current[i - 1]?.focus()
                    }}
                    className="h-12 w-10 rounded-md border border-dashed border-border bg-canvas text-center font-mono text-lg text-ink"
                  />
                ))}
              </div>
              <button
                type="submit"
                disabled={loading || code.join('').length !== 6}
                className="w-full rounded-md bg-accent py-2.5 font-mono text-xs font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
